import { useEffect, useMemo, useRef, useState } from "react";

import styles from "@/styles/containers/menu-categories.module.scss";

import { ArrowLeft, ChevronUp } from "@/components/Icons";
import { useNetLogicTableConfiguration } from "@/hooks/useNetLogicTableConfiguration";
import classNames from "classnames/bind";
import { useLocale, useTranslations } from "next-intl";
import { Brand } from "@/core/Brand";
import { CatalogHelpers } from "@/tools";
const css = classNames.bind(styles);

export default function MenuCategories({
  categories,
  onSelectCategory,
  onBack,
  searchTerm,
}) {
  const [configurations] = useNetLogicTableConfiguration();
  const t = useTranslations();
  const [filteredCategories, setFilteredCategories] = useState([]);

  const catalog_groups = Brand.catalog_groups?.INDOOR || [];

  const groups = catalog_groups.map((item) => {
    const hasItems =
      categories[item.id]?.filter((c) => c.items.length > 0) || [];

    if (hasItems.length === 0) return item;

    return {
      ...item,
      items: hasItems,
    };
  });

  if (categories["Outros produtos"]) {
    const hasItems =
      categories["Outros produtos"].filter((c) => c.items.length > 0) || [];

    groups.push({
      id: "Outros produtos",
      name: t("otherProducts"),
      items: hasItems,
    });
  }

  const filteredObjects = searchTerm
    ? Object.fromEntries(
        Object.entries(categories).filter(([key]) =>
          filteredCategories.includes(key)
        )
      )
    : categories;

  const visibleCategories = useMemo(() => {
    return Object.keys(filteredObjects)
      .map((category) => {
        return categories[category].filter((cat) => {
          const hasItems = cat.items.some((v) =>
            v.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          if (searchTerm && !hasItems) return null;
          return cat;
        });
      })
      .filter((category) => category.length > 0);
  }, [filteredObjects, categories, searchTerm]);

  const [activeCategory, setActiveCategory] = useState(
    `${visibleCategories?.[0]?.[0]?.name}-${visibleCategories?.[0]?.[0]?.id}`
  );
  const [collapsed, setCollapsed] = useState(visibleCategories?.[0]?.[0]?.id);

  useEffect(() => {
    const onSearchItemOnCatalog = () => {
      const term = searchTerm.toLowerCase();

      const filteredKeys = Object.keys(categories).filter((key) => {
        const products = categories[key];

        return products.some((product) =>
          product.items?.some((item) => item.name.toLowerCase().includes(term))
        );
      });

      setFilteredCategories(filteredKeys);
    };

    onSearchItemOnCatalog();
  }, [searchTerm]);

  useEffect(() => {
    if (activeCategory) {
      const categoriesVer = document.getElementById("categorias-vertical");

      const categoryVer = document.querySelector(
        `.${styles.category}.${styles.active}`
      );

      if (categoryVer) {
        const categoriesHalfSize =
          categoriesVer.getBoundingClientRect().height / 2;
        const categoryPlacement =
          categoryVer.getBoundingClientRect().top +
          categoriesVer.scrollTop -
          categoriesHalfSize;

        categoriesVer.scrollTo({
          top: categoryPlacement,
          behavior: "smooth",
        });
      }
    }
  }, [activeCategory]);

  const goToCategory = (e, category) => {
    setActiveCategory(`${category.name}-${category.id}`);
    onSelectCategory(category);
  };

  const filteredGroups = useMemo(() => {
    return groups.filter(
      (group) =>
        (group.items || []).length > 0 &&
        group.items.some(
          (item) => item.show === true && item.available === true
        )
    );
  }, [groups]);

  const sortedGroups = useMemo(() => {
    return filteredGroups
      .map((group) => {
        const categoriesInGroup = visibleCategories
          .flat()
          .filter((cat) => !cat.group_id || cat.group_id === group.id);

        if (categoriesInGroup.length === 0) return null;

        return {
          ...group,
          items: categoriesInGroup,
        };
      })
      .filter(Boolean);
  }, [filteredGroups, visibleCategories]);

  const sortedGroupsRef = useRef(null);

  useEffect(() => {
    if (!sortedGroupsRef.current) sortedGroupsRef.current = sortedGroups;

    if (
      JSON.stringify(sortedGroupsRef.current) !== JSON.stringify(sortedGroups)
    )
      sortedGroupsRef.current = sortedGroups;
  }, [sortedGroups]);

  useEffect(() => {
    setCollapsed(sortedGroups[0]?.id);
    onSelectCategory(sortedGroups[0]?.items?.[0]);
    setActiveCategory(
      `${sortedGroups[0]?.items?.[0]?.name}-${sortedGroups[0]?.items?.[0]?.id}`
    );
  }, [sortedGroupsRef.current]);

  useEffect(() => {
    setCollapsed(sortedGroups[0]?.id);
    onSelectCategory(sortedGroups[0]?.items?.[0]);
  }, []);

  const locale = useLocale();

  return (
    <div className={styles.menuCategories}>
      <div className={styles.header}>
        <div className={styles.back} onClick={onBack}>
          <ArrowLeft />
        </div>
        <p className={styles.table}>
          {t("table")} {configurations.externalId}
        </p>
      </div>
      {visibleCategories.length === 0 && searchTerm && (
        <p className={styles.empty}>Nenhum produto encontrado</p>
      )}
      <ul className={styles.categories} id="categorias-vertical">
        {(sortedGroups || []).map((group) => {
          if ((group.items || []).length === 0) return;

          return (
            <li className={styles.categorySection}>
              <div
                className={css({
                  category: true,
                  selected:
                    activeCategory === `${group.name}-${group.id}` ||
                    collapsed === group.id,
                })}
                onClick={() => {
                  setCollapsed(collapsed === group.id ? null : group.id);
                  onSelectCategory(group.items[0]);
                  setActiveCategory(
                    `${group.items[0].name}-${group.items[0].id}`
                  );
                }}
              >
                <div className={styles.icon}>
                  {group.icon ? (
                    <i className={`fal ${group.icon}`} />
                  ) : (
                    <i className="fal fa-bowl-hot" />
                  )}
                </div>
                <p className={styles.name}>
                  {CatalogHelpers.getTranslation({
                    item: group,
                    field: "name",
                    locale,
                  })}
                </p>
                <ChevronUp />
              </div>
              {collapsed === group.id ? (
                <ul>
                  {(group.items || []).map((item) => {
                    if (
                      !item.show ||
                      item.showAll === false ||
                      item.showTablet === false
                    )
                      return;

                    return (
                      <li
                        onClick={(e) => goToCategory(e, item)}
                        className={css({
                          active: activeCategory === `${item.name}-${item.id}`,
                        })}
                        key={item.id}
                      >
                        <p className={styles.name}>
                          {CatalogHelpers.getTranslation({
                            item,
                            field: "name",
                            locale,
                          })}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
